import React, { useRef, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  Animated,
} from 'react-native';

interface DiscoSuccessModalProps {
  visible: boolean;
  onComplete: () => void;
  duration?: number; // in milliseconds, default 2000
  navigation?: any; // navigation prop to navigate to social screen
}

export default function DiscoSuccessModal({
  visible,
  onComplete,
  duration = 2000,
  navigation,
}: DiscoSuccessModalProps) {
  // Animation values
  const discoScale = useRef(new Animated.Value(0)).current;
  const discoOpacity = useRef(new Animated.Value(0)).current;
  const sparkleOpacity = useRef(new Animated.Value(0)).current;
  const onCompleteRef = useRef(onComplete);
  const navigationRef = useRef(navigation);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    navigationRef.current = navigation;
  }, [navigation]);

  useEffect(() => {
    if (visible) {
      // Reset animation values
      discoScale.setValue(0);
      discoOpacity.setValue(0);
      sparkleOpacity.setValue(0);
      
      // Start animation sequence
      Animated.parallel([
        Animated.timing(discoScale, {
          toValue: 1,
          duration: 500,
          useNativeDriver: true,
        }),
        Animated.timing(discoOpacity, {
          toValue: 1,
          duration: 300,
          useNativeDriver: true,
        }),
      ]).start();

      // Sparkles animation
      Animated.sequence([
        Animated.timing(sparkleOpacity, {
          toValue: 1,
          duration: 300,
          useNativeDriver: true,
        }),
        Animated.timing(sparkleOpacity, {
          toValue: 0,
          duration: 1200,
          useNativeDriver: true,
        }),
      ]).start();

      // Auto-close after duration
      const timer = setTimeout(() => {
        // Navigate to Social tab if navigation is provided
        if (navigationRef.current) {
          navigationRef.current.navigate('Tabs', { screen: 'Social' });
        }
        onCompleteRef.current();
      }, duration);

      return () => clearTimeout(timer);
    } else {
      // Reset values when animation is hidden
      discoScale.setValue(0);
      discoOpacity.setValue(0);
      sparkleOpacity.setValue(0);
    }
  }, [visible, duration]);

  return (
    <Modal
      visible={visible}
      transparent={true}
      animationType="fade"
    >
      <View style={styles.discoOverlay}>
        <Animated.View
          style={[
            styles.discoContainer,
            {
              opacity: discoOpacity,
              transform: [{ scale: discoScale }]
            }
          ]}
        >
          {/* Sparkles behind disco ball */}
          <Animated.View
            style={[
              styles.sparklesContainer,
              {
                opacity: sparkleOpacity,
              }
            ]}
          >
            <Text style={styles.sparkle1}>✨</Text>
            <Text style={styles.sparkle2}>✨</Text>
            <Text style={styles.sparkle3}>✨</Text>
            <Text style={styles.sparkle4}>✨</Text>
            <Text style={styles.sparkle5}>✨</Text>
            <Text style={styles.sparkle6}>✨</Text>
          </Animated.View>
          
          <Text style={styles.discoBall}>🪩</Text>
          <Text style={styles.discoText}>Let's Disgo!</Text>
        </Animated.View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  discoOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  discoContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  sparklesContainer: {
    position: 'absolute',
    width: 200,
    height: 200,
    justifyContent: 'center',
    alignItems: 'center',
  },
  sparkle1: {
    position: 'absolute',
    fontSize: 24,
    top: 20,
    left: 30,
  },
  sparkle2: {
    position: 'absolute',
    fontSize: 20,
    top: 40,
    right: 40,
  },
  sparkle3: {
    position: 'absolute',
    fontSize: 28,
    bottom: 60,
    left: 20,
  },
  sparkle4: {
    position: 'absolute',
    fontSize: 22,
    bottom: 30,
    right: 30,
  },
  sparkle5: {
    position: 'absolute',
    fontSize: 26,
    top: 80,
    left: 10,
  },
  sparkle6: {
    position: 'absolute',
    fontSize: 24,
    top: 100,
    right: 20,
  },
  discoBall: {
    fontSize: 120,
    marginBottom: 20,
    zIndex: 1,
  },
  discoText: {
    fontSize: 32,
    fontWeight: 'bold',
    color: '#F9F5F3',
    textAlign: 'center',
  },
});
